import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './MangaGenresPage.css';

const MangaGenresPage = () => {
  const [genres, setGenres] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  // Categories for filtering genres
  const categories = [
    { id: 'all', name: 'All Genres' },
    { id: 'popular', name: 'Popular' },
    { id: 'action', name: 'Action & Adventure' },
    { id: 'drama', name: 'Drama & Slice of Life' },
    { id: 'fantasy', name: 'Fantasy & Sci-Fi' },
    { id: 'other', name: 'Other' }
  ];

  // Helper function to determine category
  const getGenreCategory = (genreName) => {
    const actionAdventure = ['Action', 'Adventure', 'Shounen', 'Martial Arts', 'Military', 'Sports'];
    const dramaSol = ['Drama', 'Slice of Life', 'Romance', 'School', 'Shoujo', 'Music', 'Josei'];
    const fantasyScifi = ['Fantasy', 'Sci-Fi', 'Magic', 'Supernatural', 'Super Power', 'Space', 'Mecha'];
    
    if (actionAdventure.includes(genreName)) return 'action';
    if (dramaSol.includes(genreName)) return 'drama';
    if (fantasyScifi.includes(genreName)) return 'fantasy';
    return 'other';
  };
  
  // Helper function to identify adult content genres
  const isAdultContent = (genreName) => {
    const adultGenres = ['Hentai', 'Ecchi', 'Adult', 'Smut'];
    return adultGenres.includes(genreName);
  };

  useEffect(() => {
    const fetchGenres = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${API_BASE}/api/manga/genres`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch manga genres');
        }
        
        const data = await response.json();
        setGenres(data);
      } catch (error) {
        console.error('Error fetching manga genres:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGenres();
  }, []);

  // Process genres into a more usable format
  const processedGenres = useMemo(() => {
    if (!genres || typeof genres !== 'object') return [];
    
    return Object.keys(genres)
      .filter(genreName => 
        genreName && 
        typeof genreName === 'string' && 
        genreName !== "null" && 
        genreName !== "undefined" && 
        Array.isArray(genres[genreName]) && 
        genres[genreName].length > 0
      )
      .map(genreName => {
        const mangaCount = genres[genreName][0]?.totalCount || genres[genreName].length;
        const sampleManga = genres[genreName][0] || {};
        
        // Get a representative image for the genre
        const imageUrl = 
          sampleManga.images?.webp?.large_image_url || 
          sampleManga.images?.jpg?.large_image_url || 
          sampleManga.images?.webp?.image_url || 
          sampleManga.image || 
          '/default-manga.jpg';
        
        return {
          name: genreName,
          count: mangaCount,
          imageUrl,
          category: getGenreCategory(genreName),
          isPopular: mangaCount > 25, // Adjust threshold as needed
          isAdult: isAdultContent(genreName)
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically
  }, [genres]);

  // Apply category filter
  const filteredGenres = useMemo(() => {
    if (activeCategory === 'all') {
      return processedGenres;
    } else if (activeCategory === 'popular') {
      return processedGenres.filter(genre => genre.isPopular);
    } else {
      return processedGenres.filter(genre => genre.category === activeCategory);
    }
  }, [processedGenres, activeCategory]);

  // Loading state
  if (loading) {
    return (
      <div className="manga-genres-page">
        <div className="manga-genres-header">
          <h1>Manga Genres</h1>
          <p>Discover manga by your favorite genres</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading manga genres...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="manga-genres-page">
        <div className="manga-genres-header">
          <h1>Manga Genres</h1>
        </div>
        <div className="error-container">
          <h3>Failed to load manga genres</h3>
          <p>{error}</p>
          <Link to="/manga" className="action-button">
            Return to Manga Home
          </Link>
        </div>
      </div>
    );
  }

  // No genres state
  if (!processedGenres || processedGenres.length === 0) {
    return (
      <div className="manga-genres-page">
        <div className="manga-genres-header">
          <h1>Manga Genres</h1>
        </div>
        <div className="no-results">
          <p>No manga genres found. Please check back later.</p>
          <Link to="/manga" className="action-button">
            Return to Manga Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="manga-genres-page">
      {/* Page Title */}
      <div className="manga-genres-page-title-container">
        <h1 className="manga-genres-page-title">Manga Genres</h1>
      </div>
      
      {/* Category Tabs */}
      <div className="genre-categories">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>
      
      {/* All Genres Grid */}
      <div className="all-genres-grid">
        {filteredGenres.map(genre => (
          <Link 
            to={`/manga/genres/${encodeURIComponent(genre.name)}`} 
            key={genre.name}
            className={`genre-card ${genre.isAdult ? 'adult-content' : ''}`}
          >
            <div className="genre-badge">
              {genre.count} titles
            </div>
            {genre.isAdult && <div className="adult-warning">18+</div>}
            <div className="genre-image-container">
              <img 
                src={genre.imageUrl} 
                alt={genre.name} 
                className="genre-image"
                onError={(e) => {
                  e.target.src = '/default-manga.jpg';
                }}
              />
              <div className="genre-overlay">
                <h3 className="genre-name">{genre.name}</h3>
                <span className="genre-count">Explore genre</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default MangaGenresPage;